import {objectFromEntries} from '@benchling/js-utils';
import isEqual from 'react-fast-compare';
import {v4 as uuidv4} from 'uuid';

import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {MinimalSchemaTag} from 'folder-items/FileConstants';
import {MetadataColumnInput, MetadataLayerApiType} from 'plates/dao/ScratchPlateDAO';
import {MAX_METADATA_LAYER_NAME_LENGTH, PlateMap, PlateMapWell} from 'samples/plate-maps/PlateMapConstants';
import {wellPositionIterator} from 'samples/plate-maps/PlateMapUtil';
import {
  getDefaultMeasurement,
  getDefaultMeasurementWithErrors,
} from 'samples/plate-maps/transfer-table/validation/FillPlateTransferTableValidationUtil';
import {PlateSchema, PlateSize} from 'samples/storables/StorableSchemaConstants';
import {FieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {getInitialTags} from 'schemas/tags/TagUtil';
import findDuplicates from 'util/findDuplicates';

// Serialized form of MinimalSchemaTag stored in persisted data frames
export interface SerializedSchemaTag {
  fieldType: FieldType;
  name: string;
  value: unknown;
}

export function serializeSchemaTag(tag: MinimalSchemaTag): SerializedSchemaTag {
  const ft = tag.tagSchemaField.fieldType;
  let value: unknown;
  if (ft === FieldType.FLOAT) {
    value = tag.floatValue ?? null;
  } else if (ft === FieldType.INTEGER) {
    value = tag.integerValue ?? null;
  } else if (ft === FieldType.BOOLEAN) {
    value = tag.booleanValue ?? null;
  } else if (ft === FieldType.DATE) {
    value = tag.dateValue ?? null;
  } else if (ft === FieldType.DATETIME) {
    value = tag.datetimeValue ?? null;
  } else if (ft === FieldType.SELECTOR) {
    value = tag.schemaFieldSelectorOptionIds ?? null;
  } else {
    value = tag.value ?? null; // TEXT, LONG_TEXT, link types
  }
  return {fieldType: ft, name: tag.tagSchemaField.name, value};
}

export function deserializeSchemaTag(serialized: SerializedSchemaTag): MinimalSchemaTag {
  const base: MinimalSchemaTag = {
    tagSchemaField: {fieldType: serialized.fieldType, name: serialized.name},
  };
  const {fieldType, value} = serialized;
  if (fieldType === FieldType.FLOAT) {
    return {...base, floatValue: value as number | null};
  } else if (fieldType === FieldType.INTEGER) {
    return {...base, integerValue: value as number | null};
  } else if (fieldType === FieldType.BOOLEAN) {
    return {...base, booleanValue: value as boolean | null};
  } else if (fieldType === FieldType.DATE) {
    return {...base, dateValue: value as string | null};
  } else if (fieldType === FieldType.DATETIME) {
    return {...base, datetimeValue: value as string | null};
  } else if (fieldType === FieldType.SELECTOR) {
    return {...base, schemaFieldSelectorOptionIds: value as Array<string> | null};
  } else {
    return {...base, value: value as string | null};
  }
}

export function deserializeSchemaTags(
  raw: Record<string, SerializedSchemaTag> | null
): PlateMapWell['schemaTags'] {
  if (raw == null) {
    return {};
  }
  return Object.fromEntries(
    Object.entries(raw).map(([id, serialized]) => [id, deserializeSchemaTag(serialized)])
  );
}

export enum ScratchPlateDesignModalStep {
  DEFINE_SETTINGS = 'DEFINE_SETTINGS',
  DEFINE_PLATE_MAP = 'DEFINE_PLATE_MAP',
}

export function getScratchPlateDesignModalSteps(): Array<{
  key: ScratchPlateDesignModalStep;
  title: string;
}> {
  return [
    {key: ScratchPlateDesignModalStep.DEFINE_SETTINGS, title: 'Define plate settings'},
    {key: ScratchPlateDesignModalStep.DEFINE_PLATE_MAP, title: 'Define plate map'},
  ];
}

export function getNextScratchPlateDesignModalStep(
  currentStepKey: ScratchPlateDesignModalStep
): ScratchPlateDesignModalStep {
  const orderedStepKeys = getScratchPlateDesignModalSteps().map((step) => step.key);
  const currentIdx = orderedStepKeys.findIndex((stepKey) => stepKey === currentStepKey);
  if (currentIdx === -1) {
    throw new Error(`Unexpected step key: ${currentStepKey}`);
  }

  const nextStep = orderedStepKeys.at(currentIdx + 1);
  if (nextStep == null) {
    throw new Error('Function called unexpectedly on last step');
  }
  return nextStep;
}

export function getPreviousScratchPlateDesignModalStep(
  currentStepKey: ScratchPlateDesignModalStep
): ScratchPlateDesignModalStep {
  const orderedStepKeys = getScratchPlateDesignModalSteps().map((step) => step.key);
  const currentIdx = orderedStepKeys.findIndex((stepKey) => stepKey === currentStepKey);
  if (currentIdx === -1) {
    throw new Error(`Unexpected step key: ${currentStepKey}`);
  } else if (currentIdx === 0) {
    throw new Error('Function called unexpectedly on first step');
  }

  return orderedStepKeys.at(currentIdx - 1)!;
}

export function isLastScratchPlateDesignModalStep(currentStepKey: ScratchPlateDesignModalStep): boolean {
  const orderedStepKeys = getScratchPlateDesignModalSteps().map((step) => step.key);
  return orderedStepKeys.at(-1) === currentStepKey;
}

export interface ScratchPlateDesignState {
  name: string | null;
  plateSize: PlateSize | null;
  plateSchema: PlateSchema | null;
  plateMap: PlateMap | null;
  plateMetadataFields?: Array<PlateMetadataFieldEntry>;
}

export const INITIAL_SCRATCH_PLATE_DESIGN_MODAL_STATE: ScratchPlateDesignState = {
  name: null,
  plateSize: null,
  plateSchema: null,
  plateMap: null,
  plateMetadataFields: [],
};

// -- Plate-level metadata fields -----------------------------------------------

export const LEAF_TYPE_TO_API_TYPE: Partial<Record<LeafType, MetadataLayerApiType>> = {
  [LeafType.TEXT]: 'text',
  [LeafType.DECIMAL]: 'float',
  [LeafType.INTEGER]: 'integer',
  [LeafType.BOOLEAN]: 'boolean',
  [LeafType.DATE]: 'date',
  [LeafType.DATETIME]: 'datetime',
};

export const MAX_PLATE_METADATA_FIELDS = 10;

// Mirrors the backend's combined per-well + plate-field cap on the metadata_layers array
// (MAX_LAYERS in benchling/plates/scratch_plates/layers/base.py).
export const MAX_SCRATCH_PLATE_METADATA_LAYERS = 15;

// Mirrors the backend's PLATE_FIELD_SYSTEM_NAME_PREFIX (benchling/plates/scratch_plates/utils/plate_fields.py).
export const PLATE_FIELD_SYSTEM_NAME_PREFIX = 'plate_field_';

/**
 * A metadata column with this prefix carries one whole-plate value duplicated onto every well's row
 * (`MetadataColumnInput.plateValue` at write time) rather than a genuine per-well value, so read paths
 * must exclude it to avoid it being misclassified as (and made editable as) an ordinary per-well layer.
 */
export function isPlateFieldSystemName(systemName: string): boolean {
  return systemName.startsWith(PLATE_FIELD_SYSTEM_NAME_PREFIX);
}

export interface PlateMetadataFieldEntry {
  // For a field seeded from a persisted plate (see PlateFieldValue in ScratchPlateDesignSummaryUtil),
  // this is the field's stable `plate_field_<uuid>` system name, round-tripped so edits rename/retype
  // it in place rather than minting a new column. A freshly-added field gets a plain uuid here instead,
  // which is not a plate-field system name and is never sent as one.
  id: string;
  name: string;
  type: LeafType;
  value: string | number | boolean | null;
}

export function buildPlateLevelMetadataLayers(
  fields: ReadonlyArray<PlateMetadataFieldEntry>
): Array<MetadataColumnInput> {
  return fields.map((field) => ({
    name: field.name,
    type: LEAF_TYPE_TO_API_TYPE[field.type] ?? 'text',
    plateValue: field.value,
    ...(isPlateFieldSystemName(field.id) ? {systemName: field.id} : {}),
  }));
}

export type PlateMetadataFieldsAction =
  | {type: 'add'}
  | {type: 'remove'; id: string}
  | {type: 'updateName'; id: string; name: string}
  | {type: 'updateType'; id: string; fieldType: LeafType}
  | {type: 'updateValue'; id: string; value: string | number | boolean | null}
  // Replaces the entire list wholesale — used to load a plate design template's fields (or clear them
  // when switching back to "Create from scratch"), as opposed to editing one field in place.
  | {type: 'load'; fields: Array<PlateMetadataFieldEntry>};

export function plateMetadataFieldsReducer(
  fields: Array<PlateMetadataFieldEntry>,
  action: PlateMetadataFieldsAction
): Array<PlateMetadataFieldEntry> {
  switch (action.type) {
    case 'add':
      return [...fields, {id: uuidv4(), name: '', type: LeafType.TEXT, value: null}];
    case 'remove':
      return fields.filter((field) => field.id !== action.id);
    case 'updateName':
      return fields.map((field) => (field.id === action.id ? {...field, name: action.name} : field));
    case 'updateType':
      return fields.map((field) =>
        field.id === action.id ? {...field, type: action.fieldType, value: null} : field
      );
    case 'updateValue':
      return fields.map((field) => (field.id === action.id ? {...field, value: action.value} : field));
    case 'load':
      return action.fields;
    default:
      return fields;
  }
}

/**
 * Validates a list of names against the rules shared by every metadata layer/field name: non-empty,
 * within `MAX_METADATA_LAYER_NAME_LENGTH`, not starting with the reserved `_` prefix, and unique.
 * `noun` (e.g. "Metadata layer", "Metadata field") is interpolated into the returned error message.
 */
export function getInvalidNamesTooltip(names: ReadonlyArray<string>, noun: string): string | null {
  const normalizedNames = new Set<string>();
  for (const rawName of names) {
    const normalizedName = rawName.trim();
    if (normalizedName.length === 0) {
      return `${noun} name must not be empty`;
    }
    if (normalizedName.length > MAX_METADATA_LAYER_NAME_LENGTH) {
      return `${noun} name must be ${MAX_METADATA_LAYER_NAME_LENGTH} characters or fewer`;
    }
    // The backend rejects '_'-prefixed metadata layer/field names as reserved (see MetadataLayer.validate
    // in benchling/plates/scratch_plates/layers/metadata.py).
    if (normalizedName.startsWith('_')) {
      return `${noun} name must not start with '_'`;
    }
    if (normalizedNames.has(normalizedName)) {
      return `${noun} names must be unique`;
    }
    normalizedNames.add(normalizedName);
  }
  return null;
}

export function getInvalidPlateMetadataFieldsTooltip(
  fields: ReadonlyArray<PlateMetadataFieldEntry>
): string | null {
  return getInvalidNamesTooltip(
    fields.map((field) => field.name),
    'Metadata field'
  );
}

/**
 * Per-well metadata layers and plate-level metadata fields are validated independently (by
 * `getInvalidMetadataLayerTooltip` and `getInvalidPlateMetadataFieldsTooltip`), but both scopes are
 * merged into one backend `metadata_layers` array sharing a single combined name-uniqueness and
 * `MAX_SCRATCH_PLATE_METADATA_LAYERS` count constraint. Checks the merged collection so a save can't
 * reach the backend with a cross-scope name collision or an over-the-combined-cap layer count.
 */
export function getInvalidMergedMetadataTooltip(
  plateMap: PlateMap | null,
  plateMetadataFields: ReadonlyArray<PlateMetadataFieldEntry>
): string | null {
  const wellLayerNames =
    plateMap?.metadataLayerInfoById != null
      ? Object.values(plateMap.metadataLayerInfoById).map((info) => info.name)
      : [];
  const fieldNames = plateMetadataFields.map((field) => field.name);
  const combinedCount = wellLayerNames.length + fieldNames.length;
  if (combinedCount > MAX_SCRATCH_PLATE_METADATA_LAYERS) {
    return (
      `A scratch plate may have at most ${MAX_SCRATCH_PLATE_METADATA_LAYERS} metadata layers and ` +
      `fields combined; ${combinedCount} were provided`
    );
  }
  // Empty names are reported by the per-scope validators, so they're excluded here.
  const normalizedNames = [...wellLayerNames, ...fieldNames]
    .map((name) => name.trim())
    .filter((name) => name.length > 0);
  if (findDuplicates(normalizedNames).length > 0) {
    return 'Metadata layer and metadata field names must be unique';
  }
  return null;
}

/**
 * Resizes a PlateMap with new dimensions, preserving well data for positions that exist in both
 * the old and new dimensions. Wells outside the new bounds are dropped; new positions are empty. Schema tags
 * are dropped.
 *
 * No-ops if the plate is not actually being resized.
 */
export function resizePlateMap(plateMap: PlateMap, newHeight: number, newWidth: number): PlateMap {
  const newSize = {numRows: newHeight, numColumns: newWidth};

  if (isEqual(plateMap.size, newSize)) {
    return plateMap;
  }

  const oldWellsByPosition = new Map<string, PlateMapWell>(
    plateMap.wells.map((well) => [`${well.position.row},${well.position.column}`, well])
  );
  const wells: Array<PlateMapWell> = [];
  for (const {row, column} of wellPositionIterator(newSize)) {
    const existing = oldWellsByPosition.get(`${row},${column}`);
    wells.push(
      existing != null
        ? {...existing, schemaTags: {}}
        : {
            position: {row, column},
            role: null,
            sources: [],
            contents: [],
            schemaTags: {},
            dataParameterTags: {},
            quantity: getDefaultMeasurement(),
            finalQuantity: getDefaultMeasurementWithErrors(),
            concentrationLayers: {},
          }
    );
  }
  return {...plateMap, size: newSize, wells};
}

/**
 * Creates an empty PlateMap from height/width, with no schemaTags defined.
 */
export function getEmptyPlateMapForScratchPlate(height: number, width: number): PlateMap {
  const size = {numRows: height, numColumns: width};
  const wells: Array<PlateMapWell> = [];
  for (const {row, column} of wellPositionIterator(size)) {
    wells.push({
      position: {row, column},
      role: null,
      sources: [],
      contents: [],
      schemaTags: {},
      dataParameterTags: {},
      quantity: getDefaultMeasurement(),
      finalQuantity: getDefaultMeasurementWithErrors(),
      concentrationLayers: {},
    });
  }
  return {
    id: null,
    size,
    wells,
    dataParameterNamesByVersionId: {},
    testOrderDilutionInfoById: {},
  };
}

export function addInitialSchemaTagsToPlateMap(
  plateMap: PlateMap,
  schemaFields: Array<SchemaField>
): PlateMap {
  const initialWellSchemaTags = objectFromEntries(
    getInitialTags(schemaFields).map((tag) => [tag.tagSchemaField.apiId, tag])
  );
  return {
    ...plateMap,
    wells: plateMap.wells.map((well) => ({...well, schemaTags: {...initialWellSchemaTags}})),
  };
}

export function removeSchemaTagsFromPlateMap(plateMap: PlateMap): PlateMap {
  return {
    ...plateMap,
    wells: plateMap.wells.map((well) => ({...well, schemaTags: {}})),
  };
}
